/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {useEffect, useMemo, useState} from 'react';
import {Spinner} from 'react-bootstrap';
import styled from 'styled-components';
import {selectGlobalVariableListState} from '../../../../redux/ducks/ScenarioAnalysis/slice';
import {useAppSelector} from '../../../../redux/hooks';
import {usePostRequest} from "../../../../api/helper";
import {MRT_ColumnDef} from 'material-react-table';
import {SCENARIO_ANALYSIS_CONSTANTS} from '../constants';
import toast from 'react-hot-toast';
import {ModelUsageComponent} from './VariablesTable';
import {renderModelNameString} from '../../../components/Text/TextFormatting';
import {ArgosTableSimple} from "../../../components/Table/ArgosTableSimple";


interface VariableModelUsageRow {
  model: string
  variation: string
}


const LoaderWrapper = styled.div({
  display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%'
})


interface FetchVariableModelUsageResponse {
  models: VariableModelUsageRow[]
}


/**
 * Renders the Model Usage content on the View Variables page
 */
const ModelUsage = () => {
  const variableData: ModelUsageComponent = useAppSelector(state => selectGlobalVariableListState(state).variableModelUsageData)
  const [variableModelUsageData, setVariableModelUsageData] = useState<any | null>([])
  const [isVariableModelUsageLoading, setIsVariableModelUsageLoading] = useState<boolean>(true)

  /**
   * Fetch model usage table content
   */
  const variableModelUsageUrl = SCENARIO_ANALYSIS_CONSTANTS.viewVariables.endpoints.variableModelUsage
  const fetchVariableModelUsageHandle = usePostRequest(variableModelUsageUrl)
  const fetchVariableModelUsage = async (variableName: string) => {
    setIsVariableModelUsageLoading(true)
    const body = {"variable": variableName}
    fetchVariableModelUsageHandle(body).then((data: FetchVariableModelUsageResponse) => {
      setVariableModelUsageData(data.models)
    }).catch((e) => {
      console.error(e)
      toast.error("An error occurred while fetching the variable model usage content")
    }).then(() => {
      setIsVariableModelUsageLoading(false)
    })
  }

  /**
   * Fetch and set model usage table content on component mount and variable change
   */
  useEffect(() => {
    fetchVariableModelUsage(variableData.variableName)
  }, [variableData.variableName])

  /**
   * Columns for Model Usage Table
   */
  const columns: MRT_ColumnDef<VariableModelUsageRow>[] = useMemo(() => [
    {
      id: 'model',
      accessorFn: (row) => renderModelNameString(row.model) ?? '',
      header: 'Model',
      sortable: true,
      center: true,
      grow: 1
    },
    {
      id: 'variation',
      accessorKey: 'variation',
      header: 'Variation',
      sortable: true,
      center: true,
      grow: 4
    },
  ], [])

  return (
    <div style={{width: '100%', height: '100%'}}>
      {isVariableModelUsageLoading ?
        <LoaderWrapper>
          <Spinner animation={'border'} style={{color: 'grey'}}/>
        </LoaderWrapper>
        :
        <ArgosTableSimple 
          columns={columns}
          data={variableModelUsageData}

          enableTopToolbar={true}
          enableBottomToolbar={true}
          enableGrouping={true}
          initialState={{ 
            grouping: ['model'],
          }}
          muiExpandButtonProps={({row}) => ({
            /* Show expand button only when row has sub-elements */
            sx: {display: row.subRows?.length ? 'flex' : 'none'}
          })}
          state={{isLoading: isVariableModelUsageLoading}}
        />
      }
    </div>
  )
}

export default ModelUsage
